import { rejectQuoteInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';

export interface RejectQuoteInput {
  readonly quoteRequestId: string;
  readonly reason: string;
}

export interface UseRejectQuoteResult {
  /** 성공하면 true. 검증 실패·서버 거절이면 false. */
  readonly reject: (input: RejectQuoteInput) => Promise<boolean>;
  readonly isPending: boolean;
  readonly fieldError: string | null;
  readonly formError: string | null;
}

const REASON_MESSAGE = '반려 사유를 1~500자로 입력해 주세요.';
const FORM_ERROR_FALLBACK = '반려하지 못했습니다.';

/**
 * 반려(quoteManagement.reject, 견적 설계 §6-6). 계약 스키마로 선검증하고, 성공하면 상세·목록을 다시 불러온다.
 * 상태 충돌이면 문구를 보이고 상세를 다시 불러와 폼 노출 조건을 새로 판단한다.
 */
export const useRejectQuote = (): UseRejectQuoteResult => {
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const invalidate = useInvalidateQuotes();
  const mutation = trpc.quoteManagement.reject.useMutation();

  const reject = async (input: RejectQuoteInput): Promise<boolean> => {
    setFieldError(null);
    setFormError(null);

    const parsed = rejectQuoteInputSchema.safeParse(input);

    if (!parsed.success) {
      setFieldError(REASON_MESSAGE);
      return false;
    }

    try {
      await mutation.mutateAsync(parsed.data);
      await Promise.all([invalidate.detail(input.quoteRequestId), invalidate.list()]);
      return true;
    } catch (cause) {
      setFormError(getErrorMessage(cause, FORM_ERROR_FALLBACK));

      if (getTrpcErrorCode(cause) === 'CONFLICT') {
        await invalidate.detail(input.quoteRequestId);
      }

      return false;
    }
  };

  return { reject, isPending: mutation.isPending, fieldError, formError };
};
